import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect } from 'react';
import { AppSkeleton } from './SkeletonLoader';

/**
 * Route guard — checks Firebase Auth state and optional RBAC role.
 * Shows skeleton loader while auth initializes, redirects to login if unauthenticated.
 */
export function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [role, setRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [offlineUnlocked, setOfflineUnlocked] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (user) {
      // Force refresh the token to ensure custom claims are up-to-date
      user.getIdToken(true)
        .then(() => user.getIdTokenResult())
        .then(tokenResult => {
          const offlineRole = localStorage.getItem('offline_role');
          setRole(tokenResult.claims.role || offlineRole || 'teacher');
          setRoleLoading(false);
        }).catch(() => {
          const cachedRole = localStorage.getItem('offline_role') || 'teacher';
          setRole(cachedRole);
          setRoleLoading(false);
        });
    } else if (!loading) {
      setRoleLoading(false);
    }
  }, [user, loading]);

  if (loading || roleLoading) {
    return <AppSkeleton />;
  }

  if (!user) {
    if (!navigator.onLine) {
      const cachedRole = localStorage.getItem('offline_role');
      const cachedPin = localStorage.getItem('offline_pin');
      
      if (cachedRole && cachedPin) {
        if (!offlineUnlocked) {
          if (!showPinModal) setShowPinModal(true);
          
          const handlePinSubmit = (e) => {
            e.preventDefault();
            if (pinInput === cachedPin) {
              setOfflineUnlocked(true);
              setShowPinModal(false);
            } else {
              setPinError('Incorrect PIN');
              setPinInput('');
            }
          };

          return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full">
                <h2 className="text-xl font-bold text-ink mb-2">Offline Unlock</h2>
                <p className="text-sm text-graphite mb-4">
                  You are offline. Enter your 4-digit PIN to access local data.
                </p>
                {pinError && <p className="text-sm text-red-600 mb-3">{pinError}</p>}
                <form onSubmit={handlePinSubmit}>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    placeholder="4-Digit PIN"
                    className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none mb-4"
                    autoFocus
                    required
                  />
                  <button
                    type="submit"
                    disabled={pinInput.length < 4}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg px-4 py-3 font-semibold transition-colors"
                  >
                    Unlock
                  </button>
                  <button
                    type="button"
                    onClick={() => window.location.href = '/login'}
                    className="w-full mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-4 py-3 font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          );
        }

        if (allowedRoles && !allowedRoles.includes(cachedRole)) {
          return <Navigate to={cachedRole === 'admin' ? '/admin/dashboard' : '/teacher/dashboard'} replace />;
        }
        return children; // Unlock successful
      }
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    // If not allowed, redirect to their appropriate dashboard
    return <Navigate to={role === 'admin' ? '/admin/dashboard' : '/teacher/dashboard'} replace />;
  }

  return children;
}
